import { IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { InstanceActivityItemModel } from '../../../../../apis/v1/instances/model';
import { Button } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useInstanceActivityInfiniteQuery } from '../hooks/useInstanceActivityInfiniteQuery';
import { downloadCsv } from '@xver/utils/csv';
import { activityActor } from '@xver/utils/projects';

import { activityRowsToCsv } from './activityFormat';
import { getActivityMeta } from './activityTypeMeta';

interface ActivityTabPanelProps {
    instanceId: number;
}

function formatActivityRowDateI18n(iso: string, t: TFunction): string {
    const d = dayjs(iso);
    if (!d.isValid()) return '-';
    const time = d.format('HH:mm');
    const now = dayjs();
    if (d.isSame(now, 'day')) return t('activity.today', { time });
    if (d.isSame(now.subtract(1, 'day'), 'day')) return t('activity.yesterday', { time });
    if (d.isSame(now, 'year')) return `${d.format('MMM D')} · ${time}`;
    return `${d.format('MMM D, YYYY')} · ${time}`;
}

function ActivityRow({ item }: { item: InstanceActivityItemModel }) {
    const { t } = useTranslation('cloudComputeInstance');
    const { Icon, color } = getActivityMeta(item.type);
    const actor = activityActor(item, t('activity.systemActor'), t('activity.someoneActor'));
    const dateLabel = formatActivityRowDateI18n(item.created_at, t);

    return (
        <div className="w-full grid grid-cols-[48px_1fr] sm:grid-cols-[160px_48px_1fr] items-start gap-3 px-4 py-3 border-t-[0.5px] border-line first:border-t-0">
            <div className="hidden sm:block text-sm text-fg/68 pt-1">{dateLabel}</div>
            <div
                className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${color}26` }}
            >
                <Icon size={16} color={color} />
            </div>
            <div className="flex flex-col min-w-0">
                <div className="text-[15px] text-fg wrap-break-word">{item.description}</div>
                <div className="text-xs text-fg/68 mt-0.5">
                    {actor}
                    <span className="sm:hidden"> · {dateLabel}</span>
                </div>
            </div>
        </div>
    );
}

function SkeletonRow() {
    return (
        <div className="w-full grid grid-cols-[48px_1fr] sm:grid-cols-[160px_48px_1fr] items-start gap-3 px-4 py-3 border-t-[0.5px] border-line first:border-t-0">
            <div className="hidden sm:block h-3 w-24 rounded bg-surface-3 animate-pulse mt-1" />
            <div className="w-8 h-8 rounded-md bg-surface-3 animate-pulse" />
            <div className="flex flex-col gap-1.5">
                <div className="h-3 w-2/3 rounded bg-surface-3 animate-pulse" />
                <div className="h-2.5 w-1/4 rounded bg-surface-3 animate-pulse" />
            </div>
        </div>
    );
}

export default function ActivityTabPanel({ instanceId }: ActivityTabPanelProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
        useInstanceActivityInfiniteQuery(instanceId, 20);

    const items = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

    const loading = isPending && !data;
    const exportDisabled = items.length === 0;

    const handleExport = () => {
        if (exportDisabled) return;
        const header = [
            t('activity.csv.createdAt'),
            t('activity.csv.type'),
            t('activity.csv.description'),
            t('activity.csv.actor'),
            t('activity.csv.ipAddress'),
        ];
        const csv = activityRowsToCsv(
            items,
            header,
            t('activity.systemActor'),
            t('activity.someoneActor')
        );
        const stamp = dayjs().format('YYYY-MM-DD');
        downloadCsv(`activity-${instanceId}-${stamp}.csv`, csv);
    };

    const exportButton = (
        <Button
            size="sm"
            variant="secondary"
            leftIcon={<IconDownload size={16} />}
            onClick={handleExport}
            disabled={exportDisabled}
        >
            {t('activity.exportCsv')}
        </Button>
    );

    return (
        <DialogBox
            label={t('activity.logTitle')}
            description={t('activity.logDescription')}
            rightDescription={exportButton}
            classNames=""
        >
            {loading ? (
                <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                </>
            ) : items.length === 0 ? (
                <div className="w-full p-8 text-sm text-fg/68 text-center">
                    {t('activity.noActivity')}
                </div>
            ) : (
                <>
                    {items.map((item) => (
                        <ActivityRow key={item.id} item={item} />
                    ))}
                    {hasNextPage && (
                        <div className="w-full flex justify-center px-4 py-3 border-t-[0.5px] border-line">
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                            >
                                {isFetchingNextPage
                                    ? t('activity.loading')
                                    : t('activity.loadMore')}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </DialogBox>
    );
}
